import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { CrudFilters, CrudSort, MetaQuery } from '@refinedev/core';
import { axiosInstance } from './axios';

class StoreCreditApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/storecredit');
  }

  async usages(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.['storeCreditId'] + '/usages';
      const response = await axiosInstance.get(`${url}`);
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  }

  async customerSummary(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const customerId = meta?.['customerId'];
    if (!customerId) {
      throw new Error('customerId is required for the customer credit summary.');
    }
    const response = await axiosInstance.get(`${this.baseURL}/customer-summary`, {
      params: { customerId },
    });
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(response.data?.status?.message || 'Unable to load the customer credit summary.');
    }
    return response.data?.data;
  }

  async returnedIncentive(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const salesChannelOrderId = meta?.['salesChannelOrderId'];
    if (!salesChannelOrderId) {
      throw new Error('salesChannelOrderId is required for returned incentive value.');
    }
    const response = await axiosInstance.get(`${this.baseURL}/returned-incentive`, {
      params: { salesChannelOrderId },
    });
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(response.data?.status?.message || 'Unable to load returned incentive value.');
    }
    return response.data?.data ?? [];
  }

  async cashoutEligibility(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const storeCreditId = meta?.['storeCreditId'];
    if (!storeCreditId) {
      throw new Error('storeCreditId is required for cashout eligibility.');
    }
    const response = await axiosInstance.get(
      `${this.baseURL}/${storeCreditId}/cashout/eligibility`,
    );
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(response.data?.status?.message || 'Unable to fetch cashout eligibility.');
    }
    return response.data?.data;
  }

  async cashout(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const storeCreditId = meta?.['storeCreditId'];
    if (!storeCreditId) {
      throw new Error('storeCreditId is required for cashout.');
    }
    const response = await axiosInstance.put(
      `${this.baseURL}/${storeCreditId}/cashout`,
      payload,
    );
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(response.data?.status?.message || 'Unable to initiate store credit cashout.');
    }
    return response.data?.data;
  }

  async cashoutRetry(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const storeCreditId = meta?.['storeCreditId'];
    if (!storeCreditId) {
      throw new Error('storeCreditId is required for cashout retry.');
    }
    const response = await axiosInstance.post(
      `${this.baseURL}/${storeCreditId}/cashout/retry`,
      payload,
    );
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(response.data?.status?.message || 'Unable to retry the store credit payout.');
    }
    return response.data?.data;
  }

  async issueCredit(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
  ): Promise<any> {
    const response = await axiosInstance.post(
      `${this.baseURL}/adhoc-issue`,
      payload,
    );
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(response.data?.status?.message || 'Unable to issue store credit.');
    }
    return response.data?.data;
  }

  async extendExpiry(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const storeCreditId = meta?.['storeCreditId'];
    if (!storeCreditId) {
      throw new Error('storeCreditId is required for expiry extension.');
    }
    const response = await axiosInstance.put(
      `${this.baseURL}/${storeCreditId}/extend-expiry`,
      payload,
    );
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(response.data?.status?.message || 'Unable to extend store credit expiry.');
    }
    return response.data?.data;
  }
}

export default StoreCreditApiClient;
